import React, { FunctionComponent, useContext, useMemo } from 'react';

import { notify as notifyAction } from '@ps-int/ps-components/src/notifications/slice';
import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../../../init-store';
import { Button, SvgImage } from '../../../../../../shared/components';
import { ButtonProps } from '../../../../../../shared/components/button';
import { NOTIFICATIONS_MAP } from '../../../../../../shared/features/notifications/ps-notifications';
import { isMobileUa } from '../../../../../../shared/features/uatraits';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { useActions } from '../../../../../../shared/hooks';
import { ShareDesktopStatus } from '../../../../constants';
import { isSidebarVisible } from '../../../../selectors';
import { NotificationId } from '../../../../types';
import { CollapseBreakpoint } from '../../constants';
import { ToolbarContext } from '../../index';
import { ToolbarOrbTooltip } from '../toolbar-orb-tooltip';
import useChangeContentOnHover from '../useChangeContentOnHover';
import classes from './style.css';

type ShareDesktopButtonProps = {
    disabled: boolean;
    status?: ShareDesktopStatus;
    onToggleShareScreen: () => void;
    buttonProps?: ButtonProps[];
    visible?: boolean;
    collapseBreakpoint?: CollapseBreakpoint | null;
};

const mapStateToProps = (state: RootState) => {
    return {
        isSidebarVisible: isSidebarVisible(state),
    };
};
const connector = connect(mapStateToProps);

const ShareDesktopButton: FunctionComponent<ConnectedProps<typeof connector> & ShareDesktopButtonProps> = ({
    buttonProps,
    onToggleShareScreen,
    disabled,
    status = ShareDesktopStatus.UNSHARING,
    visible,
    isSidebarVisible,
    collapseBreakpoint = CollapseBreakpoint.DEFAULT,
}) => {
    const { notify } = useActions({
        notify: notifyAction,
    });
    const { onMouseEnter, onMouseLeave, isHover, HoverableContent } = useChangeContentOnHover({
        defaultContent: i18n('toolbar', 'share-desktop-button-started-title'),
        onHoverContent: i18n('toolbar', 'share-desktop-button-stop-title'),
    });
    const isMobile = useSelector(isMobileUa);

    const shareScreenIcon = useMemo(() => {
        return (
            <SvgImage
                imageName={status === ShareDesktopStatus.BLOCKED ? 'shareScreenBlocked' : 'shareScreen'}
                style={{
                    width: 25,
                    height: 25,
                }}
                className={cn({ [classes.activeIcon]: status === ShareDesktopStatus.SHARING && !isHover })}
            />
        );
    }, [status, isHover]);

    const { buttonSize } = useContext(ToolbarContext);

    if (!visible) {
        return null;
    }

    const onBlockedClick = isMobile
        ? () => notify(NOTIFICATIONS_MAP[NotificationId.NO_PERMISSION_TO_SHARE_DESKTOP]())
        : undefined;

    const title =
        status === ShareDesktopStatus.SHARING
            ? i18n('share-screen', 'stop-demonstration')
            : i18n('share-screen', 'start-demonstration');

    let button = (
        <Button
            className={cn(
                classes.Button,
                {
                    [classes.sharingProcess]: status === ShareDesktopStatus.SHARING,
                    [classes.isSidebarVisible]: isSidebarVisible,
                    [classes.collapsible]: collapseBreakpoint,
                },
                collapseBreakpoint && classes[collapseBreakpoint]
            )}
            view="neutral"
            size={buttonSize}
            onClick={status !== ShareDesktopStatus.BLOCKED ? onToggleShareScreen : onBlockedClick}
            onMouseEnter={onMouseEnter}
            onHoverEnd={onMouseLeave}
            isDisabled={disabled}
            iconLeft={shareScreenIcon}
            testId={status === ShareDesktopStatus.SHARING ? 'stop-demonstration-button' : 'start-demonstration-button'}
            title={title}
            {...buttonProps}
        >
            <div className={classes.mainText}>
                {status === ShareDesktopStatus.SHARING ? (
                    <HoverableContent />
                ) : (
                    i18n('toolbar', 'share-desktop-button-title')
                )}
            </div>
        </Button>
    );

    if (status === ShareDesktopStatus.BLOCKED) {
        button = (
            <ToolbarOrbTooltip content={i18n('toolbar', 'organizer-revoked-desktop-permission')}>
                {button}
            </ToolbarOrbTooltip>
        );
    }

    return <div className={classes.buttonContainer}>{button}</div>;
};

export default connector(ShareDesktopButton);
